import { BlockOutlined, CheckCircleOutlined, RadioButtonUncheckedOutlined } from "@material-ui/icons";
import moment from "moment";
import { MachineTestResult } from "../../../models/machine-test-result.model";
import { TestRunStatus } from "../../../models/test-run.model";
import StatusChip from "../StatusChip/StatusChip";
import TestStatusChip from "../Status/StatusChip";
import Tooltip from "../Tooltip/Tooltip";
import { Grid } from "@material-ui/core";
import { StatusType } from "../../../constants/status";

export interface TestResultStatusChipsProps {
  testResult: MachineTestResult;
  dateFormat: string;
}

function getTestResultDisplayStatus(row: MachineTestResult): StatusType {
  // Excluded keeps its own distinct status here (Test History Details only) - this page's status
  // hierarchy is intentionally unchanged by the QA Details compliance status work.
  if (row.exclude) {
    return "excluded";
  }
  if (row.status === TestRunStatus.notRun) {
    return "skipped";
  }
  if (row.resolved) {
    return "failed_resolved";
  }
  if (row.status === TestRunStatus.pass) {
    return "pass";
  }
  if (row.status === TestRunStatus.fail) {
    return "fail";
  }
  if (row.status === TestRunStatus.incomplete) {
    return "incomplete";
  }
  return "pending";
}

function TestResultStatusChips({ testResult, dateFormat }: TestResultStatusChipsProps) {
  const row = testResult;

  const signoff = {
    user: row.signoffUser || row.approveUser,
    date: row.signoffDate || row.approveDate,
  };

  const resultStatus = getTestResultDisplayStatus(row);
  // Test History Details keeps its own long-standing "Not Run" wording for this status - only
  // QA Details' compliance column uses the shared "Skipped" label (see getQADetailsStatus).
  const isNotRun = resultStatus === "skipped";

  return (
    <Grid container>
      <Grid item>
        <TestStatusChip
          status={resultStatus}
          excluded={row.exclude}
          label={isNotRun ? "Not Run" : undefined}
          tooltip={isNotRun ? "This test was not run." : undefined}
        />
      </Grid>
      <Grid item>
        <Tooltip title={signoff.user ? `Signed by ${signoff.user.firstname} ${signoff.user.lastname}` : ""}>
          <span>
            <StatusChip
              icon={signoff.date ? <CheckCircleOutlined /> : <RadioButtonUncheckedOutlined />}
              status={signoff.date ? moment(signoff.date).format(dateFormat) : "Not Signed"}
              severity={row.status === row.rejectDate ? "error" : signoff.date ? "success" : undefined}
            />
          </span>
        </Tooltip>
      </Grid>
      <Grid item>
        <Tooltip
          title={
            row.approveUser
              ? `Approved by ${row.approveUser.firstname} ${row.approveUser.lastname}`
              : row.rejectUser
                ? `Rejected by ${row.rejectUser.firstname} ${row.rejectUser.lastname}`
                : ""
          }
        >
          <span>
            <StatusChip
              icon={row.approveDate ? <CheckCircleOutlined /> : row.rejectDate ? <BlockOutlined /> : <RadioButtonUncheckedOutlined />}
              status={row.approveDate || row.rejectDate ? moment(row.approveDate || row.rejectDate).format(dateFormat) : "Pending"}
              severity={row.approveDate ? "success" : row.rejectDate ? "error" : undefined}
            />
          </span>
        </Tooltip>
      </Grid>
    </Grid>
  );
}

export default TestResultStatusChips;
